import type { TFunction } from 'i18next';

/** GERP 생산비 마감(124) — 레거시 appr_edit_forms_tax_month_mstr */
export const PRODUCTION_COST_CLOSING_TYPE = 124;

export const isProductionCostClosingType = (apvType: number): boolean =>
  apvType === PRODUCTION_COST_CLOSING_TYPE;

/** 레거시 save_doc_tax_month_mstr 알럿 — 임시저장·상신 모두 */
export const PRODUCTION_COST_CLOSING_MISSING_MESSAGE =
  '데이터를 로드하지 않았습니다. 조회 조건 선택 후 확인 버튼을 누르세요.';

/** GERP 미상신 결재 건 — 키는 GERP 응답 그대로 */
export type ProductionCostClosingCandidate = {
  sysId: string;
  aprvId: string;
  aprvTypcd: string;
  title?: string | null;
  docTit?: string | null;
};

export type Value = string | number | null | undefined;

/**
 * 정산표 행 — 키는 GERP 응답 그대로(chk 1 상세·2 구분 소계).
 * 스웨거에 없는 레거시 열(시즌·BL·화폐 등)은 레거시 열 이름의 camelCase 로 올 때만 표시
 */
export type ProductionCostClosingRow = {
  reqYmd?: Value;
  maker?: Value;
  custName?: Value;
  division?: Value;
  divisionName?: Value;
  taxNo?: Value;
  chk?: Value;
  invId?: Value;
  reqAmt?: Value;
  reqVat?: Value;
  settleAmt?: Value;
  seasonName?: Value;
  busiNo?: Value;
  blNo?: Value;
  cur?: Value;
  gubunCount?: Value;
  supplyAmtCurr?: Value;
  prdAmtCurr?: Value;
  vatAmt?: Value;
  vatAmtE?: Value;
  total?: Value;
  sumCurr?: Value;
};

export type ProductionCostClosingSum = {
  total?: Value;
  sumReqAmt?: Value;
  sumReqVat?: Value;
  sumSettleAmt?: Value;
  sumSupplyAmtCurr?: Value;
};

export type ProductionCostClosingCard = {
  aprvId?: Value;
  /** N 일반 / T 관부가세 / E 부대비 */
  divisionMode?: Value;
  titles?: Value[] | null;
  rows?: ProductionCostClosingRow[] | null;
  rowsL?: ProductionCostClosingRow[] | null;
  sum?: ProductionCostClosingSum | null;
  sumL?: ProductionCostClosingSum | null;
  etNote?: Value;
};

/** 상세 라인 — 키는 GERP 응답 그대로(chk 1 라인·2 합계) */
export type ProductionCostClosingDetailLine = {
  orderNo?: Value;
  styleNo?: Value;
  enterQty?: Value;
  enterAmt?: Value;
  amtUnit?: Value;
  reqAmt?: Value;
  vat?: Value;
  chk?: Value;
  custName?: Value;
  quot?: Value;
  blNo?: Value;
  divisionName?: Value;
  settlementYmd?: Value;
  cur?: Value;
  grAmtCurr?: Value;
};

export type ProductionCostClosingDetail = {
  invId?: Value;
  brand?: Value;
  lines?: ProductionCostClosingDetailLine[] | null;
};

export type ProductionCostClosingDraftSource = {
  code: number;
  attributes: Record<string, unknown> | unknown[];
  display: {
    candidates: ProductionCostClosingCandidate[] | null;
    selection: string[] | null;
    subject: string | null;
    content: { cards?: ProductionCostClosingCard[] | null } | null;
    detail: ProductionCostClosingDetail | null;
  };
};

/** 저장 attributes 4개 + 작성 중 상세보기 조회용 마감 월 */
export type ProductionCostClosingState = {
  systemId: string;
  approvalTypeCode: string;
  approvalId: string;
  /** 작성 시점 본문 HTML */
  content: string | null;
  /** 이번 작성에서 「확인」한 마감 월(YYYYMM). 저장 안 함 — 편집 복원값은 null 이라 상세보기는 문서번호로 조회 */
  yyyymm: string | null;
};

const text = (v: unknown): string | null =>
  typeof v === 'string' && v.trim() ? v : null;

export const parseProductionCostClosingAttributes = (
  attributes: Record<string, unknown> | null | undefined,
): ProductionCostClosingState | null => {
  const systemId = text(attributes?.systemId);
  const approvalTypeCode = text(attributes?.approvalTypeCode);
  const approvalId = text(attributes?.approvalId);
  if (!attributes || !systemId || !approvalTypeCode || !approvalId) return null;
  return {
    systemId,
    approvalTypeCode,
    approvalId,
    content: text(attributes.content),
    yyyymm: null,
  };
};

export const parseProductionCostClosingContent = (
  attributes: Record<string, unknown> | null | undefined,
): string | null => text(attributes?.content);

export const buildProductionCostClosingAttributes = (
  state: ProductionCostClosingState | null | undefined,
) =>
  state
    ? {
        systemId: state.systemId,
        approvalTypeCode: state.approvalTypeCode,
        approvalId: state.approvalId,
        content: state.content,
      }
    : null;

export const checkProductionCostClosingBeforeSave = (
  state: ProductionCostClosingState | null | undefined,
): string | null =>
  state?.content ? null : PRODUCTION_COST_CLOSING_MISSING_MESSAGE;

export const productionCostClosingKey = (
  state: ProductionCostClosingState | null | undefined,
) => (state?.content ? state.approvalId : '');

/** 레거시 콤보 표시·제목 모두 DOC_TIT */
export const productionCostClosingCaseTitle = (
  c: ProductionCostClosingCandidate,
) => c.docTit?.trim() || c.title?.trim() || '';

/** 레거시: `{브랜드명} {YYYY-MM}월{마감 건 제목}마감` — 띄어쓰기 없음 그대로 */
export const buildProductionCostClosingSubject = (
  {
    brandName,
    month,
    caseTitle,
  }: { brandName: string; month: string; caseTitle: string },
  t: TFunction,
) =>
  t('{{brand}} {{month}}월{{title}}마감', {
    brand: brandName,
    month,
    title: caseTitle,
    interpolation: { escapeValue: false },
  });
